import React, { useState, useEffect } from 'react';
import { assessmentAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { FiPlus, FiX, FiClipboard, FiEye, FiTrash2 } from 'react-icons/fi';

const COLORS = ['#10b981','#f59e0b','#ef4444','#8b5cf6','#3b82f6','#ec4899'];
const sBtn = (bg='#3b82f6') => ({ padding: '10px 20px', background: bg, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter,sans-serif', display: 'inline-flex', alignItems: 'center', gap: 6 });
const sInput = { padding: '10px 14px', border: '2px solid #e2e8f0', borderRadius: 8, fontSize: 14, outline: 'none', fontFamily: 'Inter,sans-serif', width: '100%' };
const sLabel = { display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 4 };

const Assessments = () => {
  const { canEdit } = useAuth();
  const [tab, setTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [responses, setResponses] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFill, setShowFill] = useState(null);
  const [formResponses, setFormResponses] = useState({});
  const [respondentInfo, setRespondentInfo] = useState({ age: '', gender: '', zipCode: '' });

  useEffect(() => {
    const load = async () => {
      try {
        const [t, r, s] = await Promise.all([
          assessmentAPI.getTemplates(),
          assessmentAPI.getResponses({ limit: 20 }),
          assessmentAPI.getStats().catch(() => ({ data: {} }))
        ]);
        setTemplates(t.data.templates || []);
        setResponses(r.data.responses || []);
        setStats(s.data);
      } catch (e) { toast.error('Failed to load'); }
      setLoading(false);
    };
    load();
  }, []);

  const handleSubmitAssessment = async (e) => {
    e.preventDefault();
    try {
      const resArr = Object.entries(formResponses).map(([fieldId, value]) => {
        const field = showFill.fields.find(f => f.fieldId === fieldId);
        return { fieldId, label: field?.label, value };
      });
      await assessmentAPI.submitResponse({
        template: showFill._id,
        respondent: respondentInfo,
        responses: resArr
      });
      toast.success('Assessment submitted!');
      setShowFill(null);
      setFormResponses({});
      const r = await assessmentAPI.getResponses({ limit: 20 });
      setResponses(r.data.responses || []);
    } catch (e) { toast.error('Submission failed'); }
  };

  const riskColor = { low: '#10b981', moderate: '#f59e0b', high: '#ef4444', critical: '#991b1b' };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div style={{ width: 40, height: 40, border: '4px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /></div>;

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div><h1 className="page-title" style={{ fontSize: 26, fontWeight: 800 }}>Health Assessments</h1><p style={{ color: '#64748b', marginTop: 4 }}>Create and manage health assessment surveys</p></div>
      </div>

      {/* Tabs */}
      <div className="tab-bar" style={{ display: 'flex', gap: 4, background: '#f1f5f9', borderRadius: 10, padding: 4, marginBottom: 24, width: 'fit-content' }}>
        {['templates', 'responses', 'stats'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: tab === t ? '#fff' : 'transparent', fontWeight: tab === t ? 700 : 500, fontSize: 14, cursor: 'pointer', color: tab === t ? '#0f172a' : '#64748b', boxShadow: tab === t ? '0 1px 3px rgba(0,0,0,0.08)' : 'none', fontFamily: 'Inter,sans-serif', textTransform: 'capitalize' }}>{t}</button>
        ))}
      </div>

      {/* Templates Tab */}
      {tab === 'templates' && (
        <div className="assessment-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 20 }}>
          {templates.map(t => (
            <div key={t._id} style={{ background: '#fff', borderRadius: 14, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: '#eff6ff', color: '#2563eb', textTransform: 'uppercase' }}>{t.category?.replace('_', ' ')}</span>
                {t.isActive ? <span style={{ color: '#10b981', fontSize: 12, fontWeight: 600 }}>Active</span> : <span style={{ color: '#94a3b8', fontSize: 12 }}>Inactive</span>}
              </div>
              <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 8 }}>{t.title}</h3>
              <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16, lineHeight: 1.5 }}>{t.description}</p>
              <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#94a3b8', marginBottom: 16 }}>
                <span>{t.fields?.length || 0} questions</span>
                {t.estimatedTime && <span>{t.estimatedTime} min</span>}
              </div>
              <button onClick={() => { setShowFill(t); setFormResponses({}); }} style={sBtn()}>
                <FiClipboard size={14}/> Fill Assessment
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Responses Tab */}
      {tab === 'responses' && (
        <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
              {['Assessment','Risk Level','Score','Respondent','Completed By','Date'].map(h=>(
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: '#475569', fontSize: 12, textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {responses.map(r => (
                <tr key={r._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '12px 16px', fontWeight: 600 }}>{r.template?.title || '-'}</td>
                  <td style={{ padding: '12px 16px' }}><span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: `${riskColor[r.riskLevel]||'#6b7280'}20`, color: riskColor[r.riskLevel]||'#6b7280' }}>{r.riskLevel || '-'}</span></td>
                  <td style={{ padding: '12px 16px', fontWeight: 600, color: '#3b82f6' }}>{r.totalScore || '-'}</td>
                  <td style={{ padding: '12px 16px', color: '#64748b' }}>{r.respondent?.gender}, age {r.respondent?.age || '-'}</td>
                  <td style={{ padding: '12px 16px', color: '#64748b' }}>{r.completedBy?.firstName} {r.completedBy?.lastName}</td>
                  <td style={{ padding: '12px 16px', color: '#64748b' }}>{new Date(r.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Stats Tab */}
      {tab === 'stats' && stats && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>By Risk Level</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart><Pie data={(stats.byRiskLevel||[]).map(r=>({name:r._id||'Unknown',value:r.count}))} cx="50%" cy="50%" innerRadius={50} outerRadius={90} dataKey="value" label={({name,percent})=>`${name} ${(percent*100).toFixed(0)}%`}>
                {(stats.byRiskLevel||[]).map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
              </Pie><Tooltip/></PieChart>
            </ResponsiveContainer>
          </div>
          <div style={{ background: '#fff', borderRadius: 14, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>By Template</h3>
            {(stats.byTemplate||[]).map(t => (
              <div key={t._id} style={{ padding: '12px 0', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 14, fontWeight: 500 }}>{t._id}</span>
                <span style={{ fontWeight: 700, color: '#3b82f6' }}>{t.count} responses</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Fill Assessment Modal */}
      {showFill && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div className="modal-content" style={{ background: '#fff', borderRadius: 16, padding: 32, width: '100%', maxWidth: 640, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <h2 style={{ fontSize: 20, fontWeight: 800 }}>{showFill.title}</h2>
              <button onClick={() => setShowFill(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><FiX size={22}/></button>
            </div>
            <p style={{ color: '#64748b', marginBottom: 24, fontSize: 14 }}>{showFill.description}</p>

            <form onSubmit={handleSubmitAssessment}>
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: 16, marginBottom: 20 }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Respondent Info</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                  <div><label style={sLabel}>Age</label><input type="number" style={sInput} value={respondentInfo.age} onChange={e=>setRespondentInfo({...respondentInfo,age:e.target.value})}/></div>
                  <div><label style={sLabel}>Gender</label><select style={sInput} value={respondentInfo.gender} onChange={e=>setRespondentInfo({...respondentInfo,gender:e.target.value})}><option value="">Select</option><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option></select></div>
                  <div><label style={sLabel}>Zip Code</label><input style={sInput} value={respondentInfo.zipCode} onChange={e=>setRespondentInfo({...respondentInfo,zipCode:e.target.value})}/></div>
                </div>
              </div>

              {showFill.fields?.map((field, i) => (
                <div key={field.fieldId || i} style={{ marginBottom: 18 }}>
                  <label style={sLabel}>{field.label} {field.required && <span style={{ color: '#ef4444' }}>*</span>}</label>
                  {field.helpText && <p style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>{field.helpText}</p>}
                  {field.type === 'text' || field.type === 'number' ? (
                    <input type={field.type} style={sInput} value={formResponses[field.fieldId]||''} onChange={e=>setFormResponses({...formResponses,[field.fieldId]:e.target.value})} required={field.required} />
                  ) : field.type === 'textarea' ? (
                    <textarea style={{ ...sInput, minHeight: 80 }} value={formResponses[field.fieldId]||''} onChange={e=>setFormResponses({...formResponses,[field.fieldId]:e.target.value})} required={field.required} />
                  ) : field.type === 'select' || field.type === 'radio' ? (
                    <select style={sInput} value={formResponses[field.fieldId]||''} onChange={e=>setFormResponses({...formResponses,[field.fieldId]:e.target.value})} required={field.required}>
                      <option value="">Select</option>
                      {(field.options||[]).map(o=><option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : field.type === 'scale' ? (
                    <div style={{ display: 'flex', gap: 8 }}>
                      {(field.options||['1','2','3','4','5']).map(o=>(
                        <button key={o} type="button" onClick={()=>setFormResponses({...formResponses,[field.fieldId]:o})} style={{ width: 44, height: 44, borderRadius: 10, border: '2px solid', borderColor: formResponses[field.fieldId]===o?'#3b82f6':'#e2e8f0', background: formResponses[field.fieldId]===o?'#eff6ff':'#fff', color: formResponses[field.fieldId]===o?'#3b82f6':'#64748b', fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter,sans-serif' }}>{o}</button>
                      ))}
                    </div>
                  ) : (
                    <input style={sInput} value={formResponses[field.fieldId]||''} onChange={e=>setFormResponses({...formResponses,[field.fieldId]:e.target.value})} />
                  )}
                </div>
              ))}
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
                <button type="button" onClick={() => setShowFill(null)} style={{ ...sBtn('#e2e8f0'), color: '#475569' }}>Cancel</button>
                <button type="submit" style={sBtn()}>Submit Assessment</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Assessments;
